/**
 * @file components/sidebar/Sidebar.jsx
 * @description Main navigation sidebar component
 * Now uses split contexts instead of monolithic useChat()
 * 
 * @component Sidebar
 * @imports Split contexts: useAuth, useConversations, useChatSession
 */

// --- Core React and Hook Imports ---
import React, { useState, useEffect } from 'react';

// --- Import specific context hooks instead of useChat ---
import { useAuth } from '../../context/AuthContext';
import { useChatSession } from '../../context/ChatSessionContext';
import { useFileUpload } from '../../context/FileUploadContext';

// --- Style Imports ---
import styles from '../../styles/Sidebar.module.css';

// --- Child Component Imports (now from organized folders) ---
import SidebarFooter from './SidebarFooter';
import RecentChats from './RecentChats';

// --- Icon Imports ---
import { Menu, X, Plus, Search } from 'lucide-react';

/**
 * The main Sidebar component for application navigation.
 * Uses multiple specific contexts for better performance
 */
const Sidebar = ({ isMobileOpen, onMobileClose }) => {
    // --- State Management ---
    // isExtended controls the desktop expand/collapse only.
    const [isExtended, setIsExtended] = useState(false);

    // --- Pull from specific contexts ---
    // From AuthContext
    const { userId } = useAuth();

    // From ChatSessionContext
    const {
        startNewChat,
        loadConversationInChat,
        setViewMode
    } = useChatSession();

    // From FileUploadContext
    const { resetFileUpload } = useFileUpload();

    // Clears pending file attachments alongside the chat session reset
    const handleNewChat = () => {
        startNewChat();
        resetFileUpload();
    };

    console.log(`Sidebar: Rendering for user ${userId}`);

    // Lock body scroll when mobile drawer is open to prevent interaction behind it.
    useEffect(() => {
        if (isMobileOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.touchAction = 'none';
        } else {
            document.body.style.overflow = '';
            document.body.style.touchAction = '';
        }
        return () => {
            document.body.style.overflow = '';
            document.body.style.touchAction = '';
        };
    }, [isMobileOpen]);

    // If the viewport grows past the mobile breakpoint while the drawer is open,
    // close it and restore the body — avoids a permanently locked page on resize.
    useEffect(() => {
        const mq = window.matchMedia('(min-width: 769px)');
        const handleBreakpoint = (e) => { if (e.matches) onMobileClose?.(); };
        mq.addEventListener('change', handleBreakpoint);
        return () => mq.removeEventListener('change', handleBreakpoint);
    }, [onMobileClose]);

    // Closes both desktop collapse and mobile drawer.
    const performActionAndCollapse = (action) => {
        action();
        setIsExtended(false);
        onMobileClose?.();
    };

    // On mobile the menu button closes the drawer; on desktop it toggles expand/collapse.
    const handleMenuButtonClick = () => {
        if (isMobileOpen) {
            onMobileClose?.();
        } else {
            setIsExtended(prev => !prev);
        }
    };

    // On mobile the sidebar is always full-width, so treat it as "extended".
    const showText = isExtended || isMobileOpen;

    // --- Dynamic Class Names ---
    // On mobile, never apply .collapsed — its CSS rules hide labels and center icons.
    // The drawer is always full-width, so .mobileOpen alone is sufficient.
    const sidebarClass = [
        styles.sidebar,
        isMobileOpen ? styles.mobileOpen : (isExtended ? styles.extended : styles.collapsed),
    ].join(' ');

    return (
        <>
            {/* Backdrop: blocks all app interaction while mobile drawer is open. */}
            {isMobileOpen && (
                <div
                    className={styles.mobileBackdrop}
                    onClick={onMobileClose}
                    role="button"
                    aria-label="Close navigation"
                    tabIndex={-1}
                />
            )}

            <aside className={sidebarClass}>

                {/* Top section: Contains primary actions and the recent chats list. */}
                <div className={styles.top}>
                    <div className={styles.menu} onClick={handleMenuButtonClick}>
                        {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
                        {showText && <p className={styles.logoText}>Arianna</p>}
                    </div>

                    {/* New Chat Button */}
                    <div
                        className={styles.sidebarButton}
                        onClick={() => performActionAndCollapse(handleNewChat)}
                    >
                        <Plus size={24} />
                        {showText && <p>New Chat</p>}
                    </div>

                    {/* Search Button */}
                    <div
                        className={styles.sidebarButton}
                        onClick={() => performActionAndCollapse(() => setViewMode('search'))}
                    >
                        <Search size={24} />
                        {showText && <p>Search</p>}
                    </div>

                    {/* Recent chats list — visible when extended on desktop or open on mobile */}
                    {showText && (
                        <RecentChats
                            onLoadConversation={(id) => performActionAndCollapse(() => loadConversationInChat(id))}
                            onShowAll={() => performActionAndCollapse(() => setViewMode('search'))}
                            userId={userId}
                        />
                    )}
                </div>

                {/* Bottom section: Contains user profile and related actions. */}
                <div className={styles.bottom}>
                    <SidebarFooter
                        isExtended={showText}
                        onToggleExtend={() => setIsExtended(prev => !prev)}
                        onCollapse={() => { setIsExtended(false); onMobileClose?.(); }}
                    />
                </div>
            </aside>
        </>
    );
};

export default React.memo(Sidebar);